import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

import NewPage from "../../../layouts/page/NewPage";
import Form, { emptyOffer } from "./Form";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert } from "../../../components/alert/defaultAlert";
import { buildStatusFilter, DEFAULT_STATUS } from "../../../components/Common/Filter/StatusFilterSelect";
import axiosInstance from "../../../../services/AxiosInstance";
import { formatDateToYYYYMMDD, parseYYYMMDDToDate } from "../../../../utils/DateUtils";

const path = Api.subscription.club_partner;

const initialValues = () => ({
    name: '',
    category: '',
    description: '',
    // Endereço quebrado, nos nomes que o AddressRegistration espera.
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    website: '',
    instagram: '',
    facebook: '',
    whatsapp: '',
    featured: false,
    brandColor: '#fb6a21',
    offers: [],
    // Imagens escolhidas e ainda não enviadas: sobem depois que o parceiro tem id.
    logoFile: null,
    coverFile: null,
    removeLogo: false,
    removeCover: false
});

/** Oferta como a API a devolve vira o formato do formulário (datas e planos como o campo espera). */
const offerToForm = (offer) => ({
    ...emptyOffer(),
    ...offer,
    rulesList: (offer.rules || '').split(/\r?\n/).map((rule) => rule.trim()).filter(Boolean),
    validFrom: offer.validFrom ? parseYYYMMDDToDate(offer.validFrom) : null,
    validUntil: offer.validUntil ? parseYYYMMDDToDate(offer.validUntil) : null,
    plans: (offer.planIds || []).map((id, index) => ({ value: id, label: offer.planNames?.[index] || '' })),
    usageLimit: offer.usageLimit ?? ''
});

const ClubPartnerNew = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [entityData, setEntityData] = useState(null);
    const [planOptions, setPlanOptions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [company, setCompany] = useState(null);

    useEffect(() => {
        axiosInstance.post(`${Api.subscription.plan}/findByFilters`, { filters: [buildStatusFilter(DEFAULT_STATUS)] })
            .then(({ data }) => setPlanOptions((Array.isArray(data) ? data : []).map((plan) => ({ value: plan.id, label: plan.name }))))
            .catch(() => setPlanOptions([]));
        axiosInstance.get(`${path}/categories`)
            .then(({ data }) => setCategories(Array.isArray(data) ? data : []))
            .catch(() => setCategories([]));
        axiosInstance.get(Api.registration.company_info)
            .then(({ data }) => setCompany(data))
            .catch(() => setCompany(null));
    }, []);

    const validationSchema = Yup.object().shape({
        name: Yup.string().trim().required(t('validation.required.name')).max(120),
        offers: Yup.array().of(Yup.object().shape({
            title: Yup.string().trim().required('Informe o título da oferta')
        }))
    });

    /** Envia a logo e a capa escolhidas (ou remove as retiradas), já com o parceiro salvo. */
    const syncArt = async (id, values, saved) => {
        const upload = (kind, file) => {
            const body = new FormData();
            body.append('file', file);
            return axiosInstance.post(`${path}/${id}/${kind}`, body);
        };
        if (values.logoFile) await upload('logo', values.logoFile);
        else if (values.removeLogo && saved?.logoPath) await axiosInstance.delete(`${path}/${id}/logo`);
        if (values.coverFile) await upload('cover', values.coverFile);
        else if (values.removeCover && saved?.coverPath) await axiosInstance.delete(`${path}/${id}/cover`);
    };

    const formik = useFormik({
        initialValues: initialValues(),
        validationSchema,
        onSubmit: async (values) => {
            const payload = {
                name: values.name,
                category: values.category,
                description: values.description,
                address: {
                    zipCode: values.zipCode,
                    street: values.street,
                    number: values.number,
                    complement: values.complement,
                    neighborhood: values.neighborhood,
                    city: values.city,
                    state: values.state
                },
                website: values.website,
                instagram: values.instagram,
                facebook: values.facebook,
                whatsapp: values.whatsapp,
                featured: values.featured,
                brandColor: values.brandColor,
                // O status do parceiro só muda pela remoção e pelo filtro da grade.
                status: entityData?.status,
                offers: values.offers.map((offer) => ({
                    id: offer.id,
                    title: offer.title,
                    // O servidor guarda as regras num texto, uma por linha.
                    rules: offer.rulesList.map((rule) => rule.trim()).filter(Boolean).join('\n'),
                    validFrom: offer.validFrom ? formatDateToYYYYMMDD(offer.validFrom) : null,
                    validUntil: offer.validUntil ? formatDateToYYYYMMDD(offer.validUntil) : null,
                    planIds: offer.plans.map((option) => option.value),
                    usageLimit: offer.usageLimit === '' ? null : Number(offer.usageLimit),
                    usageLimitPeriod: offer.usageLimitPeriod,
                    couponValidityDays: Number(offer.couponValidityDays) || null,
                    status: offer.status
                }))
            };

            let id = entityData?.id;
            try {
                if (id) {
                    await axiosInstance.patch(`${path}/${id}`, payload);
                } else {
                    id = (await axiosInstance.post(path, payload)).data.id;
                }
            } catch (error) {
                ApiError(error);
                formik.setSubmitting(false);
                return;
            }

            try {
                await syncArt(id, values, entityData);
            } catch (error) {
                // O parceiro já está salvo: só a arte falhou, e a tela de edição deixa tentar de novo.
                ApiError(error);
                navigate(`${MenuConfig.subscription.club_partner.route}/new/${id}`);
                formik.setSubmitting(false);
                return;
            }

            // Depois de cadastrar, abre a edição: é onde o gestor gera o acesso do parceiro.
            successAlert(t('message.operation_successfully'), () => navigate(entityData
                ? MenuConfig.subscription.club_partner.route
                : `${MenuConfig.subscription.club_partner.route}/new/${id}`));
            formik.setSubmitting(false);
        }
    });

    // Preenche o formulário na edição.
    useEffect(() => {
        if (entityData) {
            formik.setValues({
                ...initialValues(),
                name: entityData.name || '',
                category: entityData.category || '',
                description: entityData.description || '',
                zipCode: entityData.address?.zipCode || '',
                street: entityData.address?.street || '',
                number: entityData.address?.number || '',
                complement: entityData.address?.complement || '',
                neighborhood: entityData.address?.neighborhood || '',
                city: entityData.address?.city || '',
                state: entityData.address?.state || '',
                website: entityData.website || '',
                instagram: entityData.instagram || '',
                facebook: entityData.facebook || '',
                whatsapp: entityData.whatsapp || '',
                featured: Boolean(entityData.featured),
                brandColor: entityData.brandColor || '#fb6a21',
                offers: (entityData.offers || []).map(offerToForm)
            });
        }
        // Só o id: recarregar o mesmo parceiro (ex.: depois de gerar o acesso) não pode apagar
        // o que o gestor já digitou no formulário.
        // eslint-disable-next-line
    }, [entityData?.id]);

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    };

    return (
        <NewPage
            motherMenu={t('subscriptions')}
            childMenu={t('club_partners')}
            activeMenu={entityData ? t('update') : t('new')}
            child={
                <Form
                    formik={formik}
                    entityData={entityData}
                    planOptions={planOptions}
                    categories={categories}
                    company={company}
                    // O acesso gerado muda o cadastro (agora tem PIN), sem tocar no que está digitado.
                    onAccessGenerated={() => setEntityData((current) => ({ ...current, accessConfigured: true }))}
                />
            }
            setEntityData={setEntityData}
            path={path}
            isSubmitting={formik.isSubmitting}
            handleSubmit={onSubmit}
            cancelRoute={MenuConfig.subscription.club_partner.route}
        />
    );
};

export default ClubPartnerNew;
